import { createRouter, createWebHistory } from "vue-router";

import routes from "./routes";
import Auth from "@/utils/auth";
import { useMetaDataStore } from "@/stores/metaDataStore";
import { useFormTemplateStore } from "@/stores/formTemplateStore";
import Constants from "@/utils/constants";
import Helper from "@/utils/helper";
import { usePermissionsStore } from "@/stores/permissionsStore";

const router = createRouter({
  history: createWebHistory(),
  routes
});

// Global navigation guard
router.beforeEach(async (to, from, next) => {
  let isAuthenticated = await Auth.isUserSignedIn();

  if (
    to.path === Constants.enums.ROUTE_ENUM.AUTH_CALLBAK ||
    to.path === Constants.enums.ROUTE_ENUM.AUTH_CALLBAK_MOBILE_APP
  ) {
    next();
  } else if (
    to.path !== Constants.enums.ROUTE_ENUM.LOG_OUT &&
    to.path !== Constants.enums.ROUTE_ENUM.LOG_IN &&
    !isAuthenticated
  ) {
    // Store the redirect path
    const queryParams = new URLSearchParams(to.query);
    queryParams.delete("email");
    const newPath = queryParams.toString()
      ? `${to.path}?${queryParams.toString()}`
      : to.path;
    useMetaDataStore().setData({ redirectPath: newPath });

    // Redirect to sign-in page if not authenticated
    next({
      path: Constants.enums.ROUTE_ENUM.LOG_IN,
      query: to.query
    });
  } else if (to.path === Constants.enums.ROUTE_ENUM.LOG_IN && isAuthenticated) {
    // Redirect to Dashboard if authenticated and navigating to login or sign-in
    next(Constants.enums.ROUTE_ENUM.HOME);
  } else {
    // Enhanced permission-based route guard
    // console.log("to", to);
    if (to.meta?.requiresAuth) {
      const permissionCheck = await checkRouteAccess(to);

      if (!permissionCheck.allowed) {
        console.warn(`Access denied to ${to.path}: ${permissionCheck.reason}`);
        // Redirect to permission denied page with the attempted route and fallback info
        next({
          path: Constants.enums.ROUTE_ENUM.PERMISSION_DENIED,
          query: {
            attemptedRoute: to.fullPath,
            fallbackRoute: permissionCheck.fallbackRoute,
            reason: permissionCheck.reason
          }
        });
        return;
      }
    }

    if (to.fullPath === "/") {
      // Manually redirect to the /home path
      next(Constants.enums.ROUTE_ENUM.HOME);
    } else {
      next();
    }
  }
});

export async function checkRouteAccess(route) {
  const { meta, path, params } = route;

  // Skip permission check if not required
  if (!meta?.requiresAuth) {
    return { allowed: true };
  }

  try {
    const permissionsStore = usePermissionsStore();
    // Get permission type from route meta
    const permissionType = meta.permissionType;
    if (!permissionType) {
      return { allowed: true }; // No permission type specified, allow access
    }

    if (
      permissionType !== Constants.enums.PERMISSION_CATEGORIES.DASHBOARD.key &&
      permissionType !== Constants.enums.PERMISSION_CATEGORIES.FORM.key
    ) {
      console.error(`Invalid permission type: ${permissionType}`);
      return {
        allowed: false,
        reason: `Invalid permission type: ${permissionType}`,
        fallbackRoute: meta.fallbackRoute || Constants.enums.ROUTE_ENUM.HOME
      };
    }

    // Super admin check
    if (
      permissionsStore.canRead(
        Constants.enums.FORM_TEMPLATE_IDS.ALL_FORMS,
        permissionType
      )
    ) {
      return { allowed: true };
    }

    // Determine the permission ID to check
    let permissionIDToCheck = meta.permissionID;

    // For dynamic routes, resolve the permission ID from the route parameters
    if (!permissionIDToCheck && params.id) {
      permissionIDToCheck = params.id;
    }

    // If no permission ID to check, allow access
    if (!permissionIDToCheck) {
      return { allowed: true };
    }

    // Check specific permission — does the user hold this grant at all (any level)?
    if (!permissionsStore.canRead(permissionIDToCheck, permissionType)) {
      return {
        allowed: false,
        reason: `No ${permissionType} permission for: ${permissionIDToCheck}`,
        fallbackRoute: meta.fallbackRoute || Constants.enums.ROUTE_ENUM.HOME
      };
    }

    // Additional gate: honor the template's configured permission_levels. The
    // store reads offline-first (in-memory cache, then local RxDB), so this is a
    // cheap local lookup. An empty/missing permission_levels imposes no extra
    // restriction (backward compatible). The level check applies the same
    // hierarchy as the rest of the guard (getCombinedPermissions includes the
    // level and above). On lookup failure we fail open — the check above already
    // passed and RLS is the real boundary.
    try {
      const { data: template } =
        await useFormTemplateStore().getFormTemplateById(permissionIDToCheck);
      const formTemplateAllowedLevels =
        template?.settings?.permission_levels || [];

      if (formTemplateAllowedLevels.length) {
        const heldAtAllowedLevel = formTemplateAllowedLevels.some((level) =>
          permissionsStore.hasPermission(
            permissionIDToCheck,
            permissionType,
            level
          )
        );
        if (!heldAtAllowedLevel) {
          return {
            allowed: false,
            reason: `Requires ${permissionType} permission at level: ${formTemplateAllowedLevels.join(
              ", "
            )}`,
            fallbackRoute: meta.fallbackRoute || Constants.enums.ROUTE_ENUM.HOME
          };
        }
      }
    } catch (levelError) {
      console.warn(
        `permission_levels check skipped for ${permissionIDToCheck}:`,
        levelError
      );
    }

    return {
      allowed: true,
      reason: null,
      fallbackRoute: meta.fallbackRoute || Constants.enums.ROUTE_ENUM.HOME
    };
  } catch (error) {
    console.error("Permission check failed:", error);
    return {
      allowed: false,
      reason: "Permission check failed",
      fallbackRoute: meta.fallbackRoute || Constants.enums.ROUTE_ENUM.HOME
    };
  }
}
export default router;
